/**
 * AdminSidebar — Global Admin Shell primary navigation.
 *
 * Renders the persistent dark left sidebar on desktop and a slide-over
 * drawer on mobile. Uses the REAL production route map — do not treat
 * the reference concept image as authoritative for destinations.
 *
 * Structural rules honoured (see Global Admin Shell spec):
 *  - Dashboard → Communication → Customers ordering is LOCKED (Section 8).
 *  - Groups exist only as subtle dividers, never verbose labels (Section 9).
 *  - Only visibly show destinations the authenticated user is permitted
 *    to reach; Gurus never see Communication (Section 32).
 *  - Identity block uses the CURRENT authenticated user — never
 *    hardcoded (Section 13-14).
 */
import React, { useEffect, useState, useMemo } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  LayoutDashboard, MessageSquare, Users, UserPlus, CalendarDays,
  Receipt, UserCog, Star, Package, BarChart3,
  ClipboardList, Settings, ChevronsLeft, ChevronsRight, LogOut,
  User as UserIcon, X, Menu, Mail, Plug, Sparkles, ChevronDown,
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import NotificationBell from './NotificationBell';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuTrigger, DropdownMenuSeparator, DropdownMenuLabel,
} from '../ui/dropdown-menu';

const COLLAPSE_KEY = 'gg_admin_sidebar_collapsed';
const API_URL = process.env.REACT_APP_BACKEND_URL;

// ---------------------------------------------------------------------------
// Navigation map — anchored to the REAL /admin/* routes shipped in App.js.
// `communication_only_admin` items are already gated in backend, but we
// hide them from the sidebar for Gurus (currently Gurus have their own
// /cleaner/* shell, so this component never renders for them anyway).
// ---------------------------------------------------------------------------
// Approved reference structure (crm-dashboard-review-v1). Relocated pages keep their routes;
// their parent item stays active through `prefix`.
const NAV_GROUPS = [
  {
    key: 'operations',
    label: 'Your day',
    items: [
      { label: 'Dashboard',      to: '/admin/dashboard',      icon: LayoutDashboard, prefix: '/admin/dashboard' },
      { label: 'Leads',          to: '/admin/quote-requests', icon: UserPlus,        prefix: '/admin/quote-requests' },
      { label: 'Clients',        to: '/admin/customers',      icon: Users,           prefix: ['/admin/customers', '/admin/clients'] },
      { label: 'Schedule',       to: '/admin/calendar',       icon: CalendarDays,    prefix: ['/admin/calendar','/admin/appointments','/admin/schedule'] },
      { label: 'Inbox',          to: '/admin/communication',  icon: MessageSquare,   prefix: ['/admin/communication','/admin/inquiries','/admin/emails','/admin/needs-review','/admin/change-requests'], badgeKey: 'inbox' },
      { label: 'Tasks',          to: '/admin/tasks',          icon: ClipboardList,   prefix: '/admin/tasks' },
    ],
  },
  {
    key: 'business',
    label: 'Your business',
    items: [
      { label: 'Team',           to: '/admin/cleaners',       icon: UserCog,         prefix: ['/admin/cleaners','/admin/hiring','/admin/performance','/admin/payouts','/admin/time-tracking','/admin/qc-templates','/admin/my-guru'] },
      { label: 'Billing',        to: '/admin/billing',        icon: Receipt,         prefix: ['/admin/billing','/admin/invoices','/admin/recurring-invoices','/admin/sent-links'] },
      { label: 'Analytics',      to: '/admin/analytics',      icon: BarChart3,       prefix: ['/admin/analytics','/admin/reports'] },
    ],
    more: [
      { label: 'Inventory',      to: '/admin/inventory',      icon: Package,         prefix: '/admin/inventory' },
      { label: 'Reviews',        to: '/admin/reviews',        icon: Star,            prefix: ['/admin/reviews','/admin/faqs'] },
      { label: 'Audiences',      to: '/admin/audiences',      icon: Mail,            prefix: '/admin/audiences' },
    ],
    after: [
      { label: 'AI Workspace',   to: '/admin/ai-workspace',   icon: Sparkles,        prefix: '/admin/ai-workspace', tag: 'NEW' },
    ],
  },
];

const BOTTOM_ITEMS = [
  { label: 'Integrations',   to: '/admin/integrations',   icon: Plug,            prefix: '/admin/integrations' },
  { label: 'Settings',       to: '/admin/settings',       icon: Settings,        prefix: ['/admin/settings','/admin/services','/admin/service-areas','/admin/audit-log','/admin/users','/admin/recycle-bin','/admin/duplicates'] },
];

const isActive = (pathname, prefix) => {
  if (Array.isArray(prefix)) return prefix.some(p => pathname === p || pathname.startsWith(p + '/'));
  return pathname === prefix || pathname.startsWith(prefix + '/');
};

const initialsFrom = (name) =>
  (name || 'GG').split(' ').filter(Boolean).map(w => w[0]).slice(0, 2).join('').toUpperCase();

// ---------------------------------------------------------------------------
// Nav item — used by both desktop sidebar and mobile drawer.
// ---------------------------------------------------------------------------
const NavItem = ({ item, active, collapsed, badgeValue, onClick }) => {
  const Icon = item.icon;
  return (
    <Link
      to={item.to}
      onClick={onClick}
      aria-current={active ? 'page' : undefined}
      data-testid={`sidebar-nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
      title={collapsed ? item.label : undefined}
      className={`group relative flex items-center gap-3 rounded-lg mx-2 my-0.5 transition-all duration-200
        ${collapsed ? 'justify-center h-9 px-0' : 'px-3 h-9'}
        ${active
          ? 'bg-[#f36a19] text-[#171912] font-bold'
          : item.tag ? 'border border-[#3a3d33] text-[#e4e6dc] hover:bg-[#292c23]' : 'text-[#bcbfb2] hover:bg-[#292c23] hover:text-white'}
      `}
    >
      {/* Orange active accent bar (left edge) */}
      <Icon className={`w-[18px] h-[18px] flex-shrink-0 transition-colors ${active ? 'text-[#171912]' : 'text-[#8e9382]'}`} />
      {!collapsed && <span className="text-[13px] leading-none tracking-tight flex-1 truncate">{item.label}</span>}
      {!collapsed && item.tag && <small className="text-[8px] font-bold tracking-wider px-1.5 py-0.5 rounded bg-[#f36a19]/20 text-[#ff985a]">{item.tag}</small>}
      {!collapsed && badgeValue > 0 && (
        <span
          data-testid={`sidebar-badge-${item.label.toLowerCase()}`}
          className={`inline-flex items-center justify-center min-w-[18px] h-4 px-1.5 rounded text-[10px] font-bold ${active ? 'bg-[#171912]/15 text-[#171912]' : 'bg-[#30352a] text-[#daddd0]'}`}
        >
          {badgeValue > 99 ? '99+' : badgeValue}
        </span>
      )}
      {collapsed && badgeValue > 0 && (
        <span className="absolute top-1.5 right-1.5 w-2 h-2 rounded-full bg-[#F56F05] ring-2 ring-zinc-900" />
      )}
    </Link>
  );
};

// ---------------------------------------------------------------------------
// Identity block at sidebar bottom — dropdown → My Guru / Portal Settings / Sign Out.
// ---------------------------------------------------------------------------
const IdentityBlock = ({ collapsed, onNavigateAway }) => {
  const { user, logout, isMasterAdmin, token } = useAuth();
  const navigate = useNavigate();
  const [photoUrl, setPhotoUrl] = useState(null);

  // Best-effort profile-photo lookup via the canonical account service.
  // Silent-fail keeps the identity block usable even if the endpoint moves.
  useEffect(() => {
    if (!user?.id || !token) return;
    let alive = true;
    (async () => {
      try {
        const r = await axios.get(`${API_URL}/api/account/me`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (alive && r.data?.photo_url) setPhotoUrl(r.data.photo_url);
      } catch { /* not fatal — fall back to initials */ }
    })();
    return () => { alive = false; };
  }, [user?.id, token]);

  const displayName = user?.full_name || user?.email || 'User';
  const shortName = displayName.split(' ').slice(0, 2).join(' ');
  const roleLabel = isMasterAdmin ? 'Master Admin' : (user?.role === 'admin' ? 'Admin' : (user?.role || ''));

  const handleLogout = async () => {
    onNavigateAway?.();
    await logout();
    navigate('/login');
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          data-testid="sidebar-identity-trigger"
          className={`w-full flex items-center gap-3 rounded-lg transition-colors text-left
            ${collapsed ? 'justify-center h-11 px-0 mx-2' : 'h-14 px-3 mx-2'}
            hover:bg-zinc-800/60 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#F56F05]
          `}
        >
          <div className="relative flex-shrink-0">
            {photoUrl ? (
              <img
                src={photoUrl}
                alt={displayName}
                className={`${collapsed ? 'w-8 h-8' : 'w-9 h-9'} rounded-full object-cover ring-2 ring-zinc-700`}
                onError={() => setPhotoUrl(null)}
              />
            ) : (
              <div className={`${collapsed ? 'w-8 h-8 text-[10px]' : 'w-9 h-9 text-xs'} rounded-full bg-[#F56F05] text-white font-bold flex items-center justify-center ring-2 ring-zinc-700`}>
                {initialsFrom(displayName)}
              </div>
            )}
          </div>
          {!collapsed && (
            <div className="flex-1 min-w-0">
              <p className="text-[13px] font-semibold text-white truncate leading-tight">{shortName}</p>
              <p className="text-[11px] text-zinc-400 truncate leading-tight mt-0.5">{roleLabel}</p>
            </div>
          )}
          {!collapsed && <ChevronDown className="w-3.5 h-3.5 text-zinc-500 flex-shrink-0" />}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent side="right" align="end" sideOffset={12} className="w-56">
        <DropdownMenuLabel className="pb-1">
          <p className="text-sm text-zinc-900 truncate">{displayName}</p>
          <p className="text-[11px] text-zinc-500 truncate font-normal">{user?.email}</p>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          onClick={() => { onNavigateAway?.(); navigate('/admin/my-guru'); }}
          data-testid="menu-my-guru"
        >
          <UserIcon className="w-4 h-4 mr-2" /> My Guru
        </DropdownMenuItem>
        {isMasterAdmin && (
          <DropdownMenuItem
            onClick={() => { onNavigateAway?.(); navigate('/admin/settings'); }}
            data-testid="menu-portal-settings"
          >
            <Settings className="w-4 h-4 mr-2" /> Portal Settings
          </DropdownMenuItem>
        )}
        <DropdownMenuSeparator />
        <DropdownMenuItem
          onClick={handleLogout}
          data-testid="menu-sign-out"
          className="text-red-600 focus:text-red-600 focus:bg-red-50"
        >
          <LogOut className="w-4 h-4 mr-2" /> Sign Out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

// ---------------------------------------------------------------------------
// Body of the sidebar — shared between desktop and mobile drawer.
// ---------------------------------------------------------------------------
const MoreTools = ({ items, collapsed, pathname, onItemClick }) => {
  const inside = items.some((it) => isActive(pathname, it.prefix));
  const [open, setOpen] = useState(inside);
  useEffect(() => { if (inside) setOpen(true); }, [inside]);
  if (collapsed) return items.map((item) => <NavItem key={item.to} item={item} active={isActive(pathname, item.prefix)} collapsed onClick={onItemClick} />);
  return (
    <div data-testid="sidebar-more-tools">
      <button type="button" onClick={() => setOpen((o) => !o)} aria-expanded={open} data-testid="sidebar-more-tools-toggle"
        className="mx-2 my-0.5 w-[calc(100%-1rem)] px-3 h-9 rounded-lg flex items-center justify-between text-[13px] text-[#bcbfb2] hover:bg-[#292c23] hover:text-white transition-colors">
        More tools <span className="text-[#8e9382]">{open ? '−' : '+'}</span>
      </button>
      {open && <div className="pl-3">{items.map((item) => <NavItem key={item.to} item={item} active={isActive(pathname, item.prefix)} collapsed={false} onClick={onItemClick} />)}</div>}
    </div>
  );
};

const SidebarBody = ({ collapsed, badges, onItemClick, showToggle, onToggleCollapse }) => {
  const { pathname } = useLocation();
  const { isMasterAdmin } = useAuth();

  const filteredGroups = useMemo(
    () => NAV_GROUPS.map(g => ({
      ...g,
      items: g.items.filter(it => !it.masterOnly || isMasterAdmin),
    })).filter(g => g.items.length),
    [isMasterAdmin],
  );
  const renderItem = (item) => (
    <NavItem key={item.to} item={item} active={isActive(pathname, item.prefix)} collapsed={collapsed}
      badgeValue={item.badgeKey ? (badges[item.badgeKey] || 0) : 0} onClick={onItemClick} />
  );
  const navRef = React.useRef(null);
  useEffect(() => { navRef.current?.querySelector('[aria-current="page"]')?.scrollIntoView({ block: 'nearest' }); }, [pathname]);

  return (
    <div className="flex flex-col h-full">
      {/* ------- brand ------- */}
      <div className={`shrink-0 border-b border-[#303229] ${collapsed ? 'py-4 flex justify-center' : 'pt-6 pb-5 px-4 text-center'}`}>
        <Link to="/admin/dashboard" onClick={onItemClick} className="inline-block focus:outline-none" data-testid="sidebar-brand">
          {collapsed
            ? <img src="/homepage2/assets/guru-head-icon-color.png" alt="Gleam Guru" className="w-9 h-9 object-contain" draggable={false} />
            : <img src="/email-assets/gleam-guru-logo.png" alt="Gleam Guru Cleaners" className="w-[134px] h-[86px] object-contain mx-auto" draggable={false} />}
        </Link>
        {!collapsed && <p className="mt-4 text-[9px] tracking-[0.19em] text-[#9b9e91] flex items-center justify-center gap-2"><i className="w-1.5 h-1.5 rounded-full bg-[#f36a19] inline-block" /> BUSINESS WORKSPACE</p>}
      </div>

      {/* ------- navigation ------- */}
      <nav
        ref={navRef}
        data-testid="sidebar-nav"
        className="flex-1 min-h-0 overflow-y-auto py-4"
        style={{ scrollbarWidth: 'thin' }}
        aria-label="Primary"
      >
        {filteredGroups.map((group) => (
          <div key={group.key} className="mb-2 last:mb-0">
            {/* Group label — collapsed mode hides text, shows a subtle top divider instead */}
            {!collapsed ? (
              <p
                data-testid={`sidebar-group-${group.key}`}
                className="px-5 mb-1.5 mt-2 text-[9px] font-bold tracking-[0.18em] uppercase text-[#85897a] select-none"
              >
                {group.label}
              </p>
            ) : (
              <div className="mx-3 mb-2 h-px bg-zinc-800/70" aria-hidden />
            )}
            {group.items.map(renderItem)}
            {group.more && <MoreTools items={group.more} collapsed={collapsed} pathname={pathname} onItemClick={onItemClick} />}
            {(group.after || []).map(renderItem)}
          </div>
        ))}
        <div className="mt-4 pt-3 border-t border-[#303229]" data-testid="sidebar-bottom-nav">{BOTTOM_ITEMS.map(renderItem)}</div>
      </nav>

      {/* ------- identity block ------- */}
      <div className="shrink-0 border-t border-zinc-800/70 py-3">
        <div className={`flex items-center gap-1 ${collapsed ? 'flex-col' : ''}`}>
          <div className="flex-1 min-w-0">
            <IdentityBlock collapsed={collapsed} onNavigateAway={onItemClick} />
          </div>
          {!collapsed && (
            <div className="pr-3">
              <NotificationBell />
            </div>
          )}
          {collapsed && (
            <div className="mt-1">
              <NotificationBell compact />
            </div>
          )}
        </div>
      </div>

      {/* ------- collapse toggle (desktop only) ------- */}
      {showToggle && (
        <button
          type="button"
          onClick={onToggleCollapse}
          data-testid="sidebar-collapse-toggle"
          aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          className={`shrink-0 mx-3 mb-3 h-8 rounded-md text-zinc-500 hover:text-white hover:bg-zinc-800/60 transition-colors inline-flex items-center justify-center ${collapsed ? '' : 'gap-2 px-3 text-[11px] tracking-wide'}`}
        >
          {collapsed ? <ChevronsRight className="w-4 h-4" /> : (<><ChevronsLeft className="w-3.5 h-3.5" /> <span>Collapse</span></>)}
        </button>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Public component. Exports the desktop sidebar + a companion mobile
// top-bar (with hamburger) + drawer. Consumers wrap this around every
// authenticated /admin/* page via AdminRoute.
// ---------------------------------------------------------------------------
export const AdminSidebar = () => {
  const { token } = useAuth();
  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem(COLLAPSE_KEY) === '1'; } catch { return false; }
  });
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [badges, setBadges] = useState({ communication: 0 });

  useEffect(() => {
    try { localStorage.setItem(COLLAPSE_KEY, collapsed ? '1' : '0'); } catch { /* noop */ }
  }, [collapsed]);

  // Live unread-conversation badge (nav item + collapsed dot indicator).
  useEffect(() => {
    if (!token) return;
    let alive = true;
    const fetchBadges = async () => {
      try {
        const r = await axios.get(`${API_URL}/api/admin/conversations/summary`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (alive) setBadges(b => ({ ...b, communication: r.data?.unread_conversations || 0, inbox: (r.data?.unread_conversations || 0) + (b.review || 0) }));
      } catch { /* silent */ }
      try {
        const r = await axios.get(`${API_URL}/api/admin/review-items/summary`, { headers: { Authorization: `Bearer ${token}` } });
        if (alive) setBadges(b => ({ ...b, review: r.data?.open || 0, inbox: (b.communication || 0) + (r.data?.open || 0) }));
      } catch { /* silent */ }
      try {
        const r = await axios.get(`${API_URL}/api/admin/inquiries/stats`, { headers: { Authorization: `Bearer ${token}` } });
        if (alive) setBadges(b => ({ ...b, inquiries: r.data?.new || 0 }));
      } catch { /* silent */ }
    };
    fetchBadges();
    const iv = setInterval(fetchBadges, 60000);
    window.addEventListener('gg-review-changed', fetchBadges);
    return () => { alive = false; clearInterval(iv); window.removeEventListener('gg-review-changed', fetchBadges); };
  }, [token]);

  // Close drawer on ESC + route change.
  const { pathname } = useLocation();
  useEffect(() => { setDrawerOpen(false); }, [pathname]);
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') setDrawerOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const desktopWidth = collapsed ? 72 : 236;

  return (
    <>
      {/* ==================== DESKTOP SIDEBAR (>= md) ==================== */}
      <aside
        data-testid="admin-sidebar-desktop"
        aria-label="Primary navigation"
        style={{ width: desktopWidth }}
        className="hidden md:flex fixed inset-y-0 left-0 z-40 bg-[#161813] text-white flex-col transition-[width] duration-200 ease-out"
      >
        <SidebarBody
          collapsed={collapsed}
          badges={badges}
          onItemClick={undefined}
          showToggle
          onToggleCollapse={() => setCollapsed(c => !c)}
        />
      </aside>

      {/* ==================== MOBILE TOP BAR ==================== */}
      <div
        data-testid="admin-mobile-topbar"
        className="md:hidden sticky top-0 z-40 bg-[#161813] text-white h-12 flex items-center justify-between px-3 border-b border-zinc-800"
      >
        <button
          type="button"
          onClick={() => setDrawerOpen(true)}
          className="inline-flex items-center justify-center w-9 h-9 rounded-md hover:bg-zinc-800"
          aria-label="Open menu"
          data-testid="admin-mobile-menu-trigger"
        >
          <Menu className="w-5 h-5" />
        </button>
        <div className="inline-flex items-center gap-2">
          <div className="w-6 h-6 rounded bg-[#F56F05] flex items-center justify-center overflow-hidden">
            <img
              src="/gleam-guru-favicon.png?v=4"
              alt="Gleam Guru"
              className="w-5 h-5 object-contain"
              draggable={false}
            />
          </div>
          <span className="text-[12px] font-bold tracking-widest">GLEAM GURU</span>
        </div>
        <span className="w-9 h-9" aria-hidden /> {/* spacer keeps title centered */}
      </div>

      {/* ==================== MOBILE DRAWER ==================== */}
      {drawerOpen && (
        <div className="md:hidden fixed inset-0 z-50" data-testid="admin-mobile-drawer">
          <div
            className="absolute inset-0 bg-black/60 backdrop-blur-sm motion-safe:animate-in motion-safe:fade-in duration-200"
            onClick={() => setDrawerOpen(false)}
            aria-hidden
          />
          <aside
            className="absolute inset-y-0 left-0 w-72 max-w-[85vw] bg-[#161813] text-white shadow-2xl motion-safe:animate-in motion-safe:slide-in-from-left duration-200"
            aria-label="Primary navigation"
          >
            <button
              type="button"
              onClick={() => setDrawerOpen(false)}
              className="absolute top-3 right-3 w-8 h-8 rounded-md hover:bg-zinc-800 inline-flex items-center justify-center z-10"
              aria-label="Close menu"
              data-testid="admin-mobile-drawer-close"
            >
              <X className="w-4 h-4" />
            </button>
            <SidebarBody
              collapsed={false}
              badges={badges}
              onItemClick={() => setDrawerOpen(false)}
              showToggle={false}
            />
          </aside>
        </div>
      )}
    </>
  );
};

/**
 * Content offset for the desktop sidebar. Pages don't need to know the
 * exact pixel value — they just render inside <AdminShell> and this
 * hook adds the correct left padding via a wrapping div. Returns 0
 * below the md breakpoint so mobile content never sits under a hidden
 * sidebar.
 */
export const useSidebarLeftOffset = () => {
  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem(COLLAPSE_KEY) === '1'; } catch { return false; }
  });
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return false;
    return window.matchMedia('(max-width: 767px)').matches;
  });

  // Sync when other sidebar instances toggle collapse.
  useEffect(() => {
    const onStorage = (e) => { if (e.key === COLLAPSE_KEY) setCollapsed(e.newValue === '1'); };
    window.addEventListener('storage', onStorage);
    // Also poll our own state (same-tab writes don't fire storage events).
    const iv = setInterval(() => {
      try {
        const v = localStorage.getItem(COLLAPSE_KEY) === '1';
        setCollapsed(prev => (prev === v ? prev : v));
      } catch { /* noop */ }
    }, 300);
    return () => { window.removeEventListener('storage', onStorage); clearInterval(iv); };
  }, []);

  // Watch viewport crossing the md breakpoint so we drop the offset on
  // mobile (where the sidebar itself is display:none).
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mql = window.matchMedia('(max-width: 767px)');
    const onChange = (e) => setIsMobile(e.matches);
    setIsMobile(mql.matches);
    if (mql.addEventListener) mql.addEventListener('change', onChange);
    else mql.addListener(onChange);
    return () => {
      if (mql.removeEventListener) mql.removeEventListener('change', onChange);
      else mql.removeListener(onChange);
    };
  }, []);

  if (isMobile) return 0;
  return collapsed ? 72 : 236;
};

export default AdminSidebar;
